import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, MousePointerClick } from "lucide-react";
import { useAcademy } from "./AcademyContext";

const PAD = 8;
const POLL_MS = 300;
const MAX_ATTEMPTS = 60; // ~18s: tempo per lasciare che l'utente apra un modale/form prima di rinunciare al passo

// Tour passo-passo "illumina il pulsante": nessuna libreria esterna. Ogni
// passo cerca il proprio elemento nel DOM finché non compare (utile quando
// un passo vive dentro un modale che il passo precedente ha appena aperto),
// e avanza da solo se l'utente clicca l'elemento evidenziato — oltre al
// pulsante "Avanti" per chi preferisce solo leggere.
export default function GuidedTour() {
  const { content, tourStep, nextTourStep, prevTourStep, stopTour } = useAcademy();
  const steps = useMemo(() => content?.tour_steps || [], [content]);
  const active = tourStep >= 0 && tourStep < steps.length;
  const [rect, setRect] = useState(null);
  const [waiting, setWaiting] = useState(false);
  const hasRect = Boolean(rect);

  const measure = useCallback(() => {
    const step = steps[tourStep];
    const el = step && document.querySelector(step.selector);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [steps, tourStep]);

  // Cerca l'elemento del passo corrente, ripetendo finché non compare (o
  // rinuncia dopo un po' e passa al successivo, così il tour non resta
  // bloccato per sempre su un elemento che non arriverà mai).
  useEffect(() => {
    if (!active) { setRect(null); setWaiting(false); return undefined; }
    let cancelled = false;
    let attempts = 0;
    const step = steps[tourStep];
    const tick = () => {
      if (cancelled) return;
      const el = step && document.querySelector(step.selector);
      if (el) {
        setWaiting(false);
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        requestAnimationFrame(measure);
        setTimeout(measure, 350);
        return;
      }
      attempts += 1;
      if (attempts === 2) setWaiting(true);
      if (attempts >= MAX_ATTEMPTS) { nextTourStep(); return; }
      timer = setTimeout(tick, POLL_MS);
    };
    let timer = setTimeout(tick, 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [active, tourStep, steps, measure, nextTourStep]);

  // Ricalcola la posizione mentre l'elemento evidenziato è visibile (resize
  // o scroll della pagina sotto il tour).
  useEffect(() => {
    if (!hasRect) return undefined;
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [hasRect, measure]);

  // Se l'utente clicca proprio l'elemento evidenziato (es. per aprire il
  // modale a cui si riferisce il passo successivo), il tour avanza da solo.
  useEffect(() => {
    if (!active) return undefined;
    const step = steps[tourStep];
    if (!step) return undefined;
    const onClickCapture = (event) => {
      const el = document.querySelector(step.selector);
      if (el && (el === event.target || el.contains(event.target))) {
        setRect(null);
        setTimeout(() => nextTourStep(), 200);
      }
    };
    document.addEventListener("click", onClickCapture, true);
    return () => document.removeEventListener("click", onClickCapture, true);
  }, [active, tourStep, steps, nextTourStep]);

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (event) => { if (event.key === "Escape") stopTour(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [active, stopTour]);

  if (!active) return null;
  const step = steps[tourStep];

  // In attesa che l'elemento compaia: nessun overlay scuro (bloccherebbe
  // proprio il pulsante che l'utente deve poter cliccare), solo un piccolo
  // avviso non invasivo con la possibilità di saltare il passo.
  if (!rect) {
    if (!waiting) return null;
    return createPortal(
      <div className="fixed inset-x-3 bottom-24 z-[110] mx-auto flex max-w-sm items-center gap-3 rounded-xl bg-navy px-4 py-3 text-white shadow-2xl sm:inset-x-auto sm:right-6" data-testid="academy-tour-waiting">
        <MousePointerClick className="h-5 w-5 shrink-0 text-gold" />
        <p className="min-w-0 flex-1 text-sm">In attesa: <strong>{step.title}</strong></p>
        <button type="button" onClick={nextTourStep} className="shrink-0 rounded-lg bg-white/15 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/25">Salta</button>
        <button type="button" onClick={stopTour} className="shrink-0 rounded-full p-1 text-white/70 hover:bg-white/10" aria-label="Chiudi tour"><X className="h-4 w-4" /></button>
      </div>,
      document.body,
    );
  }

  const boxTop = Math.max(0, rect.top - PAD);
  const boxLeft = Math.max(0, rect.left - PAD);
  const boxW = rect.width + PAD * 2;
  const boxH = rect.height + PAD * 2;

  const viewportH = window.innerHeight;
  const viewportW = window.innerWidth;
  const tooltipWidth = Math.min(320, viewportW - 24);
  // Stima prudente dell'altezza del tooltip: usata solo per tenerlo dentro
  // lo schermo anche quando l'elemento evidenziato è più alto o più basso
  // del viewport (es. un elenco lungo) — non deve mai finire fuori dalla
  // vista, altrimenti i suoi pulsanti "Avanti"/"Fine" non sono cliccabili.
  const tooltipHeightEstimate = 220;
  const spaceBelow = viewportH - (boxTop + boxH);
  const placeBelow = spaceBelow > tooltipHeightEstimate || boxTop < tooltipHeightEstimate;
  const rawTop = placeBelow ? boxTop + boxH + 10 : boxTop - 10 - tooltipHeightEstimate;
  const tooltipTop = Math.min(Math.max(rawTop, 12), Math.max(12, viewportH - tooltipHeightEstimate - 12));
  const tooltipLeft = Math.min(Math.max(12, boxLeft), viewportW - tooltipWidth - 12);

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[110]" role="dialog" aria-modal="true" aria-label="Tour guidato">
      <div className="pointer-events-auto absolute inset-x-0 top-0 bg-navy/70" style={{ height: boxTop }} />
      <div className="pointer-events-auto absolute inset-x-0 bg-navy/70" style={{ top: boxTop + boxH, bottom: 0 }} />
      <div className="pointer-events-auto absolute bg-navy/70" style={{ top: boxTop, height: boxH, left: 0, width: boxLeft }} />
      <div className="pointer-events-auto absolute bg-navy/70" style={{ top: boxTop, height: boxH, left: boxLeft + boxW, right: 0 }} />
      <div className="pointer-events-none absolute rounded-lg ring-4 ring-gold" style={{ top: boxTop, left: boxLeft, width: boxW, height: boxH }} />

      <div className="pointer-events-auto absolute max-h-[220px] overflow-y-auto rounded-xl bg-white p-4 shadow-2xl" style={{ top: tooltipTop, left: tooltipLeft, width: tooltipWidth }} data-testid="academy-tour-tooltip">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading text-base font-bold text-navy">{step.title}</h3>
          <button type="button" onClick={stopTour} className="shrink-0 rounded-full p-1 text-slate-400 hover:bg-slate-100" aria-label="Chiudi tour"><X className="h-4 w-4" /></button>
        </div>
        {step.body && <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{step.body}</p>}
        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="text-xs text-slate-400">Passo {tourStep + 1} di {steps.length}</span>
          <div className="flex gap-2">
            {tourStep > 0 && <button type="button" onClick={prevTourStep} className="rounded-lg border border-hairline px-3 py-1.5 text-sm font-medium text-navy hover:bg-slate-50">Indietro</button>}
            <button type="button" onClick={nextTourStep} className="rounded-lg bg-navy px-3 py-1.5 text-sm font-semibold text-white hover:bg-navy/90" data-testid="academy-tour-next">{tourStep + 1 >= steps.length ? "Fine" : "Avanti"}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
